import * as React from 'react';

import { Button, NumberField, Row } from '@rematter/ui-web';
import { LucideCheck, LucideRefreshCw, LucideX } from 'lucide-react';

import type { CycleCountLineRecord } from '../../_graphql/queryCycleCountTask';
import { useTranslations } from '../../i18n/useTranslations';

import { VarianceIndicator } from './VarianceIndicator';

type CountLineRowProps = {
  line: CycleCountLineRecord;
  isBlindCount: boolean;
  isEditable: boolean;
  showLineActions: boolean;
  onCountedQuantityChange: (lineId: string, value: number | null) => void;
  onApproveAdjustment?: (lineId: string, unitCostOverride?: number) => void;
  onRejectAdjustment?: (lineId: string) => void;
  onRequestRecount?: (lineId: string) => void;
  localCountedQuantity?: number | null;
};

const formatQuantity = (value: number | null, unit?: string): string => {
  if (value == null) return '\u2014';
  const formatted = value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return unit ? `${formatted} ${unit}` : formatted;
};

export const CountLineRow = ({
  line,
  isBlindCount,
  isEditable,
  showLineActions,
  onCountedQuantityChange,
  onApproveAdjustment,
  onRejectAdjustment,
  onRequestRecount,
  localCountedQuantity,
}: CountLineRowProps) => {
  const t = useTranslations();

  const displayedCountedQuantity =
    localCountedQuantity !== undefined
      ? localCountedQuantity
      : line.countedQuantity;

  const computedVariance = React.useMemo(() => {
    if (displayedCountedQuantity == null) {
      return { quantity: null, percentage: null, withinTolerance: null };
    }
    const expected = line.expectedQuantity ?? 0;
    const qty = displayedCountedQuantity - expected;
    if (expected === 0) {
      return { quantity: qty, percentage: null, withinTolerance: null };
    }
    const pct = (qty / expected) * 100;
    const within = Math.abs(pct) <= line.toleranceThreshold;
    return { quantity: qty, percentage: pct, withinTolerance: within };
  }, [
    displayedCountedQuantity,
    line.expectedQuantity,
    line.toleranceThreshold,
  ]);

  const varianceQuantity =
    localCountedQuantity !== undefined
      ? computedVariance.quantity
      : line.varianceQuantity;
  const variancePercentage =
    localCountedQuantity !== undefined
      ? computedVariance.percentage
      : line.variancePercentage;
  const withinTolerance =
    localCountedQuantity !== undefined
      ? computedVariance.withinTolerance
      : line.withinTolerance;

  const [localCostOverride, setLocalCostOverride] = React.useState<
    number | undefined
  >(undefined);

  React.useEffect(() => {
    if (
      line.adjustmentStatus === 'Approved' ||
      line.adjustmentStatus === 'Rejected'
    ) {
      setLocalCostOverride(undefined);
    }
  }, [line.adjustmentStatus]);

  const canApprove =
    showLineActions &&
    onApproveAdjustment &&
    line.adjustmentStatus !== 'Approved' &&
    line.adjustmentStatus !== 'Rejected';

  return (
    <tr className="@CountLineRow border-b border-neutral/10 last:border-b-0">
      <td className="px-3 py-2.5">
        <div className="flex flex-col">
          <span className="text-sm font-medium">{line.itemName}</span>
          {line.itemSku && (
            <span className="text-xs text-neutral/50">{line.itemSku}</span>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-sm text-neutral/60">
        {line.stockAccountName || '\u2014'}
      </td>
      <td className="px-3 py-2.5 text-sm tabular-nums">
        {isBlindCount ? (
          <span className="text-neutral/30">
            {t.cycleCountTasksView.detail.hidden}
          </span>
        ) : (
          formatQuantity(line.expectedQuantity, line.expectedQuantityUnit)
        )}
      </td>
      <td className="px-3 py-2.5">
        {isEditable ? (
          <NumberField
            value={displayedCountedQuantity ?? undefined}
            onChange={(val) => onCountedQuantityChange(line.id, val ?? null)}
            className="w-28"
            aria-label={t.cycleCountTasksView.detail.countedQuantity}
          />
        ) : (
          <span className="text-sm tabular-nums">
            {formatQuantity(
              displayedCountedQuantity,
              line.countedQuantityUnit ?? line.expectedQuantityUnit,
            )}
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 text-sm tabular-nums">
        {formatQuantity(varianceQuantity)}
      </td>
      <td className="px-3 py-2.5">
        <VarianceIndicator
          variancePercentage={variancePercentage}
          withinTolerance={withinTolerance}
        />
      </td>
      <td className="px-3 py-2.5 text-sm text-neutral/60">
        {line.adjustmentStatus ?? '\u2014'}
      </td>
      {showLineActions && (
        <td className="px-3 py-2.5">
          <Row gap2>
            {canApprove && (
              <NumberField
                value={localCostOverride}
                onChange={(val) => setLocalCostOverride(val ?? undefined)}
                className="w-24"
                minValue={0}
                label={t.cycleCountTasksView.detail.lineActions.costOverride}
              />
            )}
            {canApprove && (
              <Button
                size="small"
                variant="outline"
                color="success"
                icon={<LucideCheck />}
                onPress={() => onApproveAdjustment(line.id, localCostOverride)}
                aria-label={t.cycleCountTasksView.detail.lineActions.approve}
              />
            )}
            {onRejectAdjustment &&
              line.adjustmentStatus !== 'Approved' &&
              line.adjustmentStatus !== 'Rejected' && (
                <Button
                  size="small"
                  variant="outline"
                  color="failure"
                  icon={<LucideX />}
                  onPress={() => onRejectAdjustment(line.id)}
                  aria-label={t.cycleCountTasksView.detail.lineActions.reject}
                />
              )}
            {onRequestRecount &&
              !line.recountRequested &&
              line.adjustmentStatus !== 'Approved' &&
              line.adjustmentStatus !== 'Rejected' && (
                <Button
                  size="small"
                  variant="outline"
                  color="warning"
                  icon={<LucideRefreshCw />}
                  onPress={() => onRequestRecount(line.id)}
                  aria-label={t.cycleCountTasksView.detail.lineActions.recount}
                />
              )}
            {line.recountRequested && (
              <span className="text-xs text-warning">
                {t.cycleCountTasksView.detail.lineActions.recountRequested}
              </span>
            )}
          </Row>
        </td>
      )}
    </tr>
  );
};
